
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { OrderWithCustomer } from '../types/orderTypes';
import { createOrderViaEndpoint } from '@/lib/api/orderService';
import { OrderProduct } from '../types/orderTypes';
import { supabase } from '@/lib/supabase/client';

interface UseOrderSubmissionProps {
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerCountry: string;
  customerRegion: string;
  customerMunicipality: string;
  orderNumber: string;
  orderDate: string;
  shippingAddress: string;
  notes: string;
  products: OrderProduct[];
  totalAmount: number;
  organizationId: string;
  handleOrderAdded: (order: OrderWithCustomer) => void;
  handleBackToOrders: () => void;
  shopId?: string;
  currency?: string;
}

export const useOrderSubmission = ({
  customerId,
  customerName,
  customerEmail,
  customerPhone,
  customerCountry,
  customerRegion,
  customerMunicipality,
  orderNumber,
  orderDate,
  shippingAddress,
  notes,
  products,
  totalAmount,
  organizationId,
  handleOrderAdded,
  handleBackToOrders,
  shopId,
  currency = "DZD" // Valeur par défaut
}: UseOrderSubmissionProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation de base
    if (!customerPhone || products.length === 0) {
      toast({
        title: "Erreur de validation",
        description: "Un client et au moins un produit sont requis",
        variant: "destructive",
      });
      return;
    }
    
    // Vérification du numéro de commande
    if (!orderNumber || orderNumber.trim() === '') {
      toast({
        title: "Erreur de validation",
        description: "Le numéro de commande est requis",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Log customer data for debugging
      console.log("SUBMIT: Customer data:", { 
        id: customerId, 
        name: customerName, 
        phone: customerPhone,
        country: customerCountry || 'Algeria',
        region: customerRegion,
        municipality: customerMunicipality,
        shippingAddress: shippingAddress
      });
      console.log("SUBMIT: Order number:", orderNumber);
      console.log("SUBMIT: Shipping address:", shippingAddress);
      
      // All validations passed
      console.log("SUBMIT: Toutes les validations sont passées, début de la création de commande");
      
      // Préparer les métadonnées pour le client et l'expédition
      const customerMetadata = {
        email: customerEmail,
        phone: customerPhone,
        name: customerName
      };
      
      const shippingMetadata = {
        address: shippingAddress,
        country: customerCountry || 'Algeria',
        region: customerRegion,
        municipality: customerMunicipality
      };
      
      // Créer l'ordre en utilisant l'API
      const response = await createOrderViaEndpoint({
        organization_id: organizationId,
        customer: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone,
          country: customerCountry || 'Algeria',
          region: customerRegion,
          municipality: customerMunicipality,
          address: shippingAddress
        },
        items: products.map(product => ({
          product_id: product.product_id,
          product_name: product.name,
          quantity: product.quantity,
          price: product.price,
          currency: product.currency || currency // Utiliser la devise du produit ou la devise par défaut
        })),
        external_id: orderNumber,
        shop_id: shopId,
        currency: currency, // Définir la devise pour toute la commande
        customer_metadata: customerMetadata,
        shipping_metadata: shippingMetadata,
        order_metadata: { notes: notes }
      });
      
      console.log("SUBMIT: Réponse API reçue:", response);
      
      if (response) {
        // Construction de l'objet de commande à retourner
        const newOrder: OrderWithCustomer = {
          id: response.id,
          customer_id: response.customer_id,
          organization_id: organizationId,
          order_date: response.order_date || orderDate,
          total_amount: totalAmount,
          status: response.status || 'pending',
          payment_status: response.payment_status || 'unpaid',
          created_at: response.created_at || new Date().toISOString(),
          updated_at: response.updated_at || new Date().toISOString(),
          order_items: products.map(p => ({
            id: '',
            name: p.name,
            price: p.price,
            quantity: p.quantity,
            total: p.total,
            product_id: p.product_id,
            product_name: p.product_name,
            currency: p.currency || currency // Inclure la devise dans les items de la commande
          })),
          customer_name: customerName, // Inclure le nom du client
          shop_id: shopId || response.shop_id,
          order_number: orderNumber,
          currency: response.currency || currency, // Inclure la devise de la commande
          notes: notes, // Inclure les notes
          customer_metadata: customerMetadata,
          shipping_metadata: shippingMetadata,
          order_metadata: { notes: notes }
        };
        
        handleOrderAdded(newOrder);
        handleBackToOrders();
      } else {
        throw new Error("La réponse de l'API est vide ou invalide");
      }
    } catch (error) {
      console.error("Erreur lors de la création de la commande:", error);
      toast({
        title: "Erreur",
        description: "Impossible de créer la commande. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleSubmit
  };
};
